/**
 * Toast — transient feedback for actions that already happened.
 *
 * Mount <ToastProvider> once (App.tsx already does) and call useToast() anywhere:
 *
 *   const toast = useToast();
 *   toast.success('Run queued', { description: `Run ${runId} is resolving.` });
 *   toast.error(errorMessage(err));
 *
 * Rules of use:
 *  - A toast is not a confirmation. Anything destructive goes through ConfirmDialog
 *    first; the toast only reports the outcome.
 *  - Errors stay 8s (and can be dismissed); successes go at 4s. An error that the
 *    operator must act on should be rendered in the page, not thrown at a toast.
 *  - Never put a token, a full phone number or a borrower name in one — contract §8.6.
 *    Mask with format.maskToken / maskPhone / maskName first.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export type ToastTone = 'ok' | 'danger' | 'warn' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  description?: ReactNode;
  /** Milliseconds. 0 keeps it until dismissed. */
  duration?: number;
  action?: ToastAction;
}

export interface ToastRecord extends ToastOptions {
  id: number;
  tone: ToastTone;
  message: ReactNode;
  leaving?: boolean;
}

export interface ToastApi {
  success: (message: ReactNode, options?: ToastOptions) => number;
  error: (message: ReactNode, options?: ToastOptions) => number;
  warn: (message: ReactNode, options?: ToastOptions) => number;
  info: (message: ReactNode, options?: ToastOptions) => number;
  show: (tone: ToastTone, message: ReactNode, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
  clear: () => void;
}

const DEFAULT_DURATION: Record<ToastTone, number> = {
  ok: 4000,
  info: 4000,
  warn: 6000,
  danger: 8000,
};

const GLYPH: Record<ToastTone, string> = {
  ok: '✓',
  danger: '✕',
  warn: '▲',
  info: 'i',
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const remove = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      // Mark leaving so the exit animation runs, then drop it.
      setToasts((current) => current.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
      const timer = setTimeout(() => remove(id), 180);
      timers.current.set(id, timer);
    },
    [remove],
  );

  const show = useCallback(
    (tone: ToastTone, message: ReactNode, options: ToastOptions = {}) => {
      const id = nextId.current;
      nextId.current += 1;

      setToasts((current) => {
        // Hard cap: a failing poll loop must not paper the screen.
        const next = [...current, { ...options, id, tone, message }];
        return next.length > 4 ? next.slice(next.length - 4) : next;
      });

      const duration = options.duration ?? DEFAULT_DURATION[tone];
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message, options) => show('ok', message, options),
      error: (message, options) => show('danger', message, options),
      warn: (message, options) => show('warn', message, options),
      info: (message, options) => show('info', message, options),
      dismiss,
      clear: () => setToasts([]),
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-region" role="region" aria-label="Notifications">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast tone-${toast.tone} ${toast.leaving ? 'is-leaving' : ''}`}
            role={toast.tone === 'danger' ? 'alert' : 'status'}
            aria-live={toast.tone === 'danger' ? 'assertive' : 'polite'}
          >
            <span className="toast-glyph" aria-hidden="true">
              {GLYPH[toast.tone]}
            </span>
            <div className="toast-body">
              <div className="toast-message">{toast.message}</div>
              {toast.description ? <div className="toast-description">{toast.description}</div> : null}
              {toast.action ? (
                <div className="toast-actions">
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      toast.action?.onClick();
                      dismiss(toast.id);
                    }}
                  >
                    {toast.action.label}
                  </button>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-icon"
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss notification"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Throws when used outside the provider — that is a wiring bug, not a runtime case. */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast() requires <ToastProvider> — it is mounted in App.tsx.');
  return api;
}

export default ToastProvider;
